import type {MouseEventHandler, ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {useSelectedTab} from '@Components/tabs';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {clearSelectedItems, getSelectedGridItems, reloadGrid, toggleGridItemSelection} from '@Components/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {UserMediaVOResponse} from '@Libraries/user-media-library';
import {getMediaStorageForbackendData, UserMediaType} from '@Libraries/user-media-library';
import {
  COLLECTION_MEDIA_GRID_ID,
  UploadsMediaSubTab,
  UploadsMediaType,
  USER_UPLOADS_MEDIA_SUB_TABS,
  USER_UPLOADS_MEDIA_TABS,
  USER_UPLOADS_SELECTION_GRID_ID,
} from '@Panels/user-media-panel/user-media-panel.types';
import {LoadingError} from '@Panels/user-media-panel/components/loading-error';
import type {ElementData} from '@Libraries/add-media-library';
import {UserImageSource} from '@Libraries/user-image-library';
import {NoMediaSection} from '@Panels/user-media-panel/components/no-media-section';
import {CollectionMediaGrid} from '@Panels/user-media-panel/components/collection-media-grid';
import {PhotosGrid} from '@Panels/user-media-panel/components/photos-grid';
import {VideosGrid} from '@Panels/user-media-panel/components/videos-grid';
import {BackgroundImagesGrid} from '@Panels/user-media-panel/components/background-images-grid';
import {AudiosGrid} from '@Panels/user-media-panel/components/audios-grid';
import {getGridIdForTabStates} from '@Panels/user-media-panel/user-media.library';
import {NoAiImages} from '@Panels/user-media-panel/components/no-ai-images/no-ai-images';
import {Button, Type} from '@Components/button';
import {showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@/libraries/pmw-stock-media-library';
import styles from './media-section.module.scss';
import {UserAudioSource} from '@Libraries/user-audio-library';

interface MediaSectionProps {
  maxSelectionNumber?: number;
  addInBulk: boolean;
  excludeItemWithExtension?: string;
  defaultTab: UploadsMediaType;
  mediaTypesToShow: UploadsMediaType[];
  scrollingContainer: HTMLDivElement | null;
  videoQualityFiltersDisabled?: boolean;
  onGenerateAIImage?: MouseEventHandler;
  shouldLoadDataUrlImage?: boolean;

  onClose(): void;

  addSelectedItems(items?: ElementData[]): void;
}

export function MediaSection({videoQualityFiltersDisabled = false, shouldLoadDataUrlImage = false, ...props}: MediaSectionProps): ReactElement | null {
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS) as UploadsMediaSubTab;
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, props.defaultTab) as UploadsMediaType;
  const gridStorageItems = useAppSelector((state) => {
    return state.grids.gridsHashmap[currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab)]?.items;
  });
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, USER_UPLOADS_SELECTION_GRID_ID);
  });
  const unlinkedMediaItems = useAppSelector((state) => {
    return state.mediaUploads.unlinkedMedia;
  });
  const dispatch = useAppDispatch();

  const reloadActiveGrids = (): void => {
    setIsMediaLoaded(false);
    dispatch(
      reloadGrid({
        gridId: getGridIdForTabStates(activeTab, activeSubtab),
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
      })
    );
    dispatch(
      clearSelectedItems({
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
      })
    );
  };

  useEffect(reloadActiveGrids, [isUserLoggedIn(), unlinkedMediaItems.length]);

  useEffect(() => {
    setIsMediaLoaded(false);
    dispatch(
      clearSelectedItems({
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
      })
    );
    if (currentCollectionId) {
      dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS));
    } else {
      dispatch(
        reloadGrid({
          gridId: COLLECTION_MEDIA_GRID_ID,
          gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
        })
      );
    }
  }, [currentCollectionId]);

  useEffect(() => {
    setIsMediaLoaded(false);
  }, [activeTab, activeSubtab]);

  const getUploadsMediaTypeFromGridItemType = (type: GRID_ITEM_TYPE, source: string): UploadsMediaType => {
    if (source === UserImageSource.BACKGROUND_UPLOAD) {
      return UploadsMediaType.IMAGE_BACKGROUND;
    }
    switch (type) {
      case GRID_ITEM_TYPE.USER_IMAGE:
        return UploadsMediaType.IMAGE;
      case GRID_ITEM_TYPE.USER_VIDEO:
        return UploadsMediaType.VIDEO;
      case GRID_ITEM_TYPE.USER_AUDIO:
        return UploadsMediaType.AUDIO;
      default:
        throw new Error(`Invalid media type for user media modal: ${type}`);
    }
  };

  const getUnlinkedMediaForActiveTab = (): GridItemStorage[] => {
    return unlinkedMediaItems.filter((item) => {
      return getUploadsMediaTypeFromGridItemType(item.type, item.source as string) === activeTab;
    });
  };

  const getItems = async (getterParams: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    if (!isUserLoggedIn()) {
      const gridItems = getUnlinkedMediaForActiveTab();
      setIsMediaLoaded(true);
      return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
    }

    let data: UserMediaVOResponse[] = [];
    try {
      if (activeTab === UploadsMediaType.IMAGE && activeSubtab === UploadsMediaSubTab.AI_IMAGE) {
        data = (await window.PMW.readLocal('stockmedia/getGeneratedImagesInUploadsByUser', {
          o: Object.keys(getterParams.offsets).reduce((agg, item) => {
            return agg + getterParams.offsets[item];
          }, 0),
          l: getterParams.limit,
        })) as UserMediaVOResponse[];
      } else if (activeTab === UploadsMediaType.AUDIO && activeSubtab === UploadsMediaSubTab.AI_VOICE) {
        data = (await window.PMW.readLocal('aivoice/getGeneratedAudiosInUploadsByUser', {
          o: Object.keys(getterParams.offsets).reduce((agg, item) => {
            return agg + getterParams.offsets[item];
          }, 0),
          l: getterParams.limit,
        })) as UserMediaVOResponse[];
      } else {
        data = (await window.PMW.readLocal('collection/getUserMultiMediaItemsInCollection', {
          o: Object.keys(getterParams.offsets).reduce((agg, item) => {
            return agg + getterParams.offsets[item];
          }, 0),
          l: getterParams.limit,
          c: currentCollectionId,
          collectionType: activeTab,
        })) as UserMediaVOResponse[];
      }
    } catch (e) {
      setErrorLoading(true);
    }

    const MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE = {
      [UploadsMediaType.IMAGE]: UserMediaType.IMAGE,
      [UploadsMediaType.IMAGE_BACKGROUND]: UserMediaType.IMAGE_BACKGROUND,
      [UploadsMediaType.VIDEO]: UserMediaType.VIDEO,
      [UploadsMediaType.AUDIO]: UserMediaType.AUDIO,
    };

    const gridItems = [];
    for (const itemData of data) {
      gridItems.push(getMediaStorageForbackendData(itemData, MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE[activeTab]));
    }

    setIsMediaLoaded(true);
    return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
  };

  const onGridItemClicked = (gridItem: GridItemStorage, isDoubleClick: boolean): void => {
    if (isDoubleClick && selectedGridItems.length === 1 && selectedGridItems[0].id === gridItem.id) {
      props.onClose();
      props.addSelectedItems();
      return;
    }

    dispatch(
      toggleGridItemSelection({
        gridItemId: gridItem.id,
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
      })
    );
  };

  const isGridItemSourceAIImage = (gridItem: GridItemStorage): boolean => {
    return gridItem.source === UserImageSource.AI_TEXT_TO_IMAGE;
  };

  const isGridItemSourceAIVoice = (gridItem: GridItemStorage): boolean => {
    return gridItem.source === UserAudioSource.TEXT_TO_SPEECH;
  };

  const hasGridItems = (): boolean => {
    if (isUserLoggedIn()) {
      if (activeSubtab === UploadsMediaSubTab.AI_IMAGE) {
        return !!gridStorageItems?.filter(isGridItemSourceAIImage).length;
      }
      if (activeSubtab === UploadsMediaSubTab.AI_VOICE) {
        return !!gridStorageItems?.filter(isGridItemSourceAIVoice).length;
      }
      return !!gridStorageItems?.length;
    }
    return !!getUnlinkedMediaForActiveTab().length;
  };

  if (isMediaLoaded && !hasGridItems() && !errorLoading) {
    if (activeSubtab === UploadsMediaSubTab.UPLOADS) {
      return (
        <NoMediaSection
          videoQualityFiltersDisabled={videoQualityFiltersDisabled}
          maxSelectionNumber={props.maxSelectionNumber}
          mediaTypesToShow={props.mediaTypesToShow}
          addSelectedItems={(items?: ElementData[]) => {
            props.addSelectedItems(items);
          }}
          addInBulk={props.addInBulk}
          shouldLoadDataUrlImage={shouldLoadDataUrlImage}
          onClose={(): void => {
            props.onClose();
          }}
        />
      );
    }
    if (activeSubtab === UploadsMediaSubTab.AI_IMAGE) {
      return (
        <NoAiImages>
          <Button
            icon={isUserPremium() ? 'icon-ai-image' : undefined}
            iconClassName={isUserPremium() ? styles.aiImageButton : undefined}
            customClasses="spacing-m-t-5"
            text={isUserPremium() ? window.i18next.t('pmwjs_generate') : window.i18next.t('pmwjs_upgrade')}
            type={Type.SECONDARY}
            onClick={(e: React.MouseEvent): void => {
              if (isUserPremium() && props.onGenerateAIImage) {
                props.onGenerateAIImage(e);
              } else {
                void showTextToImageUpsellDialog();
              }
            }}
          />
        </NoAiImages>
      );
    }
  }

  if (errorLoading) {
    return (
      <LoadingError
        onRefresh={(): void => {
          setErrorLoading(false);
          setIsMediaLoaded(true);
        }}
      />
    );
  }

  const getUploadsGrid = (): ReactElement => {
    const gridProps = {
      getItems,
      onGridItemClicked,
      gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
      showLoadingSkeletonItems: isUserLoggedIn(),
      scrollingContainer: props.scrollingContainer,
      excludeItemWithExtension: props.excludeItemWithExtension,
      maxSelectionNumber: props.maxSelectionNumber,
      className: 'spacing-p-t-6 spacing-p-b-6',
      clearGridOnUnmount: true,
    };
    if (currentCollectionId) {
      return <CollectionMediaGrid {...gridProps} />;
    }
    switch (activeTab) {
      case UploadsMediaType.IMAGE:
        return <PhotosGrid {...gridProps} id={getGridIdForTabStates(activeTab, activeSubtab)} key={getGridIdForTabStates(activeTab, activeSubtab)} />;
      case UploadsMediaType.VIDEO:
        return <VideosGrid {...gridProps} />;
      case UploadsMediaType.AUDIO:
        return <AudiosGrid {...gridProps} id={getGridIdForTabStates(activeTab, activeSubtab)} key={getGridIdForTabStates(activeTab, activeSubtab)} />;
      case UploadsMediaType.IMAGE_BACKGROUND:
        return <BackgroundImagesGrid {...gridProps} />;
      default:
        throw new Error(`Invalid media type ${activeTab}`);
    }
  };

  const shouldShowGrid = (): boolean => {
    return (
      (activeSubtab === UploadsMediaSubTab.UPLOADS || activeSubtab === UploadsMediaSubTab.AI_VOICE || activeSubtab === UploadsMediaSubTab.AI_IMAGE) && !!props.scrollingContainer
    );
  };

  return shouldShowGrid() ? getUploadsGrid() : null;
}
